import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog"
import { Button } from "./ui/button"
import { Badge } from "./ui/badge"
import { 
  Loader2, 
  Upload, 
  AlertCircle, 
  CheckCircle2, 
  FileSpreadsheet,
  MapPin,
  Eye,
  CheckCircle,
  XCircle,
  ArrowRight,
  ArrowLeft,
  Download,
  Info,
  AlertTriangle
} from "lucide-react"
import { Alert, AlertDescription } from "./ui/alert"
import * as XLSX from 'xlsx'

export default function BulkUploadModal({ 
  isOpen, 
  onClose, 
  onUpload, 
  isUploading, 
  title = "Bulk Upload",
  config,
  uploadResult
}) {
  const [step, setStep] = useState(1) // 1: upload, 2: map, 3: preview, 4: result
  const [file, setFile] = useState(null)
  const [excelData, setExcelData] = useState([])
  const [excelColumns, setExcelColumns] = useState([])
  const [columnMap, setColumnMap] = useState({})
  const [validationResult, setValidationResult] = useState(null)
  const [error, setError] = useState(null)
  const [headerRow, setHeaderRow] = useState(1)
  const [startRow, setStartRow] = useState(2)
  const [rawRows, setRawRows] = useState([])
  const [showProceedOption, setShowProceedOption] = useState(false)

  // Construir columnas requeridas y opcionales del sistema
  const systemColumns = [
    ...config.requiredFields.map(field => ({
      key: field,
      label: config.fieldMappings[field].label,
      required: true
    })),
    ...Object.entries(config.fieldMappings)
      .filter(([key]) => !config.requiredFields.includes(key))
      .map(([key, value]) => ({
        key,
        label: value.label,
        required: false
      }))
  ]

  const requiredKeys = config.requiredFields
  const hasInvalidHeaders = excelColumns.some(col => col.startsWith("__EMPTY"))

  const handleFileChange = (e) => {
    setError(null)
    const f = e.target.files[0]
    if (!f) return
    setFile(f)
    const reader = new FileReader()
    reader.onload = (evt) => {
      const bstr = evt.target.result
      const wb = XLSX.read(bstr, { type: 'binary' })
      const wsname = wb.SheetNames[0]
      const ws = wb.Sheets[wsname]
      const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" })
      setRawRows(raw)
      processExcelWithHeaderRow(raw, 1)
    }
    reader.readAsBinaryString(f)
  }

  const processExcelWithHeaderRow = (raw, headerRowIdx) => {
    const headers = raw[headerRowIdx - 1] || []
    const dataRows = raw.slice(startRow - 1)

    const data = dataRows.map(row => {
      const obj = {}
      headers.forEach((h, i) => {
        obj[h || `__EMPTY_${i}`] = row[i]
      })
      return obj
    })

    setExcelData(data)
    setExcelColumns(headers.map((h, i) => h || `__EMPTY_${i}`))
    setHeaderRow(headerRowIdx)
    
    const initialMap = {}
    systemColumns.forEach(sysCol => {
      const found = headers.find(h => h && h.toLowerCase() === sysCol.key.toLowerCase())
      if (found) initialMap[sysCol.key] = found
    })
    setColumnMap(initialMap)
    setStep(2)
  }

  const handleMapChange = (sysKey, excelKey) => {
    setColumnMap(prev => ({ ...prev, [sysKey]: excelKey }))
  }

  const canContinueMapping = requiredKeys.every(key => columnMap[key] && columnMap[key] !== "")

  const handleValidate = async () => {
    setError(null)
    setShowProceedOption(false)
    try {
      const mappedData = excelData.map(row => {
        const obj = {}
        systemColumns.forEach(col => {
          if (columnMap[col.key]) {
            const value = row[columnMap[col.key]]
            obj[col.key] = value === "" || value === undefined ? null : value
          }
        })
        return obj
      }).filter(item => Object.values(item).some(value => value !== null))

      const validItems = []
      const invalidItems = []
      const allErrors = []

      mappedData.forEach((item, index) => {
        const validation = config.validateItem(item)
        if (validation.isValid) {
          validItems.push(item)
        } else {
          invalidItems.push({ item, errors: validation.errors, index })
          allErrors.push(...validation.errors.map(error => `Item ${index + 1}: ${error}`))
        }
      })

      const hasErrors = allErrors.length > 0
      const canProceed = validItems.length > 0

      setValidationResult({
        success: !hasErrors,
        errors: allErrors,
        data: {
          valid: validItems,
          invalid: invalidItems
        },
        canProceedWithValidItems: hasErrors && canProceed
      })

      if (hasErrors && canProceed) {
        setShowProceedOption(true)
      }

      setStep(3)
    } catch (err) {
      setError("Error validating data. Please check the format and try again.")
    }
  }

  const handleUpload = async (proceedWithValidItemsOnly = false) => {
    if (!file) {
      setError('Please select a file')
      return
    }

    try {
      const mappedData = excelData.map(row => {
        const obj = {}
        systemColumns.forEach(col => {
          if (columnMap[col.key]) {
            const value = row[columnMap[col.key]]
            obj[col.key] = value === "" || value === undefined ? null : value
          }
        })
        return obj
      }).filter(item => Object.values(item).some(value => value !== null))

      await onUpload(mappedData, { proceedWithValidItemsOnly })
      setStep(4)
    } catch (error) {
      setError('Error processing file')
    }
  }

  const reset = () => {
    setStep(1)
    setFile(null)
    setExcelData([])
    setExcelColumns([])
    setColumnMap({})
    setValidationResult(null)
    setError(null)
    setHeaderRow(1)
    setStartRow(2)
    setRawRows([])
    setShowProceedOption(false)
  }

  const unusedExcelColumns = excelColumns.filter(ec => !Object.values(columnMap).includes(ec))

  // Step indicator component
  const StepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      <div className="flex items-center space-x-4">
        {[
          { num: 1, label: "Upload", icon: Upload },
          { num: 2, label: "Map", icon: MapPin },
          { num: 3, label: "Preview", icon: Eye },
          { num: 4, label: "Complete", icon: CheckCircle }
        ].map((s, idx) => (
          <div key={s.num} className="flex items-center">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${
              step >= s.num 
                ? 'bg-gradient-to-r from-orange-500 to-orange-600 border-orange-500 text-white shadow-lg' 
                : 'border-slate-300 text-slate-400 bg-white'
            }`}>
              {step > s.num ? (
                <CheckCircle className="h-5 w-5" />
              ) : (
                <s.icon className="h-5 w-5" />
              )}
            </div>
            <span className={`ml-2 text-sm font-medium ${step >= s.num ? 'text-slate-900' : 'text-slate-500'}`}>
              {s.label}
            </span>
            {idx < 3 && (
              <ArrowRight className={`h-4 w-4 mx-4 ${step > s.num ? 'text-orange-500' : 'text-slate-300'}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  )

  return (
    <Dialog open={isOpen} onOpenChange={v => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-[95vw] w-[1400px] h-[90vh] overflow-hidden bg-gradient-to-br from-slate-50 to-white">
        <DialogHeader className="border-b border-slate-200 pb-6">
          <DialogTitle className="text-2xl font-bold text-slate-900 flex items-center">
            <FileSpreadsheet className="h-6 w-6 text-orange-500 mr-3" />
            {title}
          </DialogTitle>
          <p className="text-slate-600 mt-2">Import your data seamlessly with our step-by-step wizard</p>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1">
          <StepIndicator />

          {/* Step 1: Upload */}
          {step === 1 && (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded shadow-sm border border-slate-200 p-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Upload className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Upload Your Excel File</h3>
                  <p className="text-slate-600 mb-8">Select an Excel file (.xlsx or .xls) containing your data</p>
                  
                  <div className="relative">
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      id="file-upload"
                    />
                    <label 
                      htmlFor="file-upload"
                      className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-orange-300 rounded hover:border-orange-400 transition-colors cursor-pointer bg-orange-50/50 hover:bg-orange-50"
                    >
                      <FileSpreadsheet className="h-12 w-12 text-orange-500 mb-4" />
                      <span className="text-lg font-medium text-slate-700">Click to select file</span>
                      <span className="text-sm text-slate-500 mt-1">or drag and drop your Excel file here</span>
                    </label>
                  </div>
                  
                  {file && (
                    <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded">
                      <div className="flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                        <span className="font-medium text-green-800">{file.name}</span>
                      </div>
                    </div>
                  )}
                  
                  {error && (
                    <Alert className="mt-6 border-red-200 bg-red-50">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                      <AlertDescription className="text-red-800">{error}</AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Mapping */}
          {step === 2 && (
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
              {/* Left Column - Configuration */}
              <div className="space-y-6">
                {/* File Configuration */}
                <div className="bg-white rounded shadow-sm border border-slate-200 p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                      <Info className="h-4 w-4 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">File Configuration</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Header Row</label>
                      <select
                        className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors"
                        value={headerRow}
                        onChange={e => processExcelWithHeaderRow(rawRows, parseInt(e.target.value))}
                      >
                        {rawRows.slice(0, 10).map((row, idx) => (
                          <option key={idx} value={idx + 1}>
                            Row {idx + 1}: {row.join(' | ').slice(0, 50)}...
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Data Start Row</label>
                      <select
                        className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors"
                        value={startRow}
                        onChange={e => setStartRow(parseInt(e.target.value))}
                      >
                        {rawRows.slice(0, 10).map((row, idx) => (
                          <option key={idx} value={idx + 1}>
                            Row {idx + 1}: {row.join(' | ').slice(0, 50)}...
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Invalid Headers Warning */}
                {hasInvalidHeaders && (
                  <Alert className="border-yellow-200 bg-yellow-50">
                    <AlertTriangle className="h-4 w-4 text-yellow-600" />
                    <AlertDescription className="text-yellow-800">
                      The Excel file has invalid headers. Please select the row containing the actual column names.
                    </AlertDescription>
                  </Alert>
                )}

                {/* Column Mapping */}
                <div className="bg-white rounded shadow-sm border border-slate-200 p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center mr-3">
                      <MapPin className="h-4 w-4 text-purple-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900">Map Excel Columns</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {systemColumns.map(col => (
                      <div key={col.key} className="space-y-2">
                        <label className="flex items-center text-sm font-medium text-slate-700">
                          {col.label}
                          {col.required && (
                            <Badge variant="destructive" className="ml-2 text-xs">Required</Badge>
                          )}
                        </label>
                        <select
                          className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-200 transition-colors"
                          value={columnMap[col.key] || ""}
                          onChange={e => handleMapChange(col.key, e.target.value)}
                        >
                          <option value="">Select column...</option>
                          {excelColumns.map(ec => (
                            <option key={ec} value={ec}>{ec}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" onClick={reset} className="flex items-center">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Another File
                  </Button>
                  <Button variant="outline" onClick={() => setStep(1)} className="flex items-center">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white flex items-center"
                    onClick={() => setStep(3)}
                    disabled={!canContinueMapping}
                  >
                    Next: Preview
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>

                {/* Unmapped Columns */}
                {unusedExcelColumns.length > 0 && (
                  <Alert className="border-blue-200 bg-blue-50">
                    <Info className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      <span className="font-medium">Unmapped Excel columns:</span><br />
                      {unusedExcelColumns.join(", ")}
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              {/* Right Column - Preview */}
              <div className="bg-white rounded shadow-sm border border-slate-200 p-6">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center mr-3">
                    <Eye className="h-4 w-4 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">Data Preview</h3>
                </div>
                
                <div className="overflow-hidden rounded border border-slate-200">
                  <div className="overflow-x-auto max-h-96">
                    <table className="min-w-full divide-y divide-slate-200">
                      <thead className="bg-slate-50">
                        <tr>
                          {excelColumns.map(col => (
                            <th key={col} className="px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-slate-200">
                        {excelData.slice(0, 10).map((row, i) => (
                          <tr key={i} className="hover:bg-slate-50 transition-colors">
                            {excelColumns.map(col => (
                              <td key={col} className="px-4 py-3 text-sm text-slate-900 whitespace-nowrap">
                                {row[col] || '-'}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                
                {excelData.length > 10 && (
                  <p className="text-xs text-slate-500 mt-3 text-center">
                    Showing first 10 rows of {excelData.length} total rows
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Preview & Validation */}
          {step === 3 && (
            <div className="space-y-6">
              {/* Header */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">Review Mapped Data</h3>
                  <div className="flex flex-wrap gap-2">
                    {systemColumns.map(col => (
                      <Badge
                        key={col.key}
                        variant={columnMap[col.key] ? "default" : "secondary"}
                        className={columnMap[col.key] ? "bg-green-100 text-green-800 border-green-200" : ""}
                      >
                        {col.label}
                        {columnMap[col.key] && <CheckCircle className="h-3 w-3 ml-1" />}
                      </Badge>
                    ))}
                  </div>
                </div>
                
                <Button 
                  onClick={handleValidate} 
                  disabled={isUploading || excelData.length === 0} 
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                >
                  {isUploading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Validate Data
                </Button>
              </div>

              {/* Validation Results */}
              {validationResult && (
                <div className="space-y-6">
                  {/* Status Header */}
                  <div className={`rounded p-6 border ${
                    validationResult.success 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-center">
                      {validationResult.success ? (
                        <CheckCircle className="h-8 w-8 text-green-600 mr-4" />
                      ) : (
                        <XCircle className="h-8 w-8 text-red-600 mr-4" />
                      )}
                      <div>
                        <h4 className={`text-lg font-semibold ${
                          validationResult.success ? 'text-green-900' : 'text-red-900'
                        }`}>
                          {validationResult.success ? 'Validation Successful!' : 'Validation Issues Found'}
                        </h4>
                        <p className={`text-sm ${
                          validationResult.success ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {validationResult.success 
                            ? 'All items are valid and ready to upload'
                            : `${validationResult.data?.valid?.length || 0} valid items, ${validationResult.data?.invalid?.length || 0} items with errors`
                          }
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Errors List */}
                  {validationResult.errors && validationResult.errors.length > 0 && (
                    <div className="bg-white rounded shadow-sm border border-red-200 p-6">
                      <h5 className="font-semibold text-red-900 mb-3 flex items-center">
                        <AlertCircle className="h-5 w-5 mr-2" />
                        Validation Errors
                      </h5>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {validationResult.errors.map((err, idx) => (
                          <div key={idx} className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">
                            {err}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Proceed Option */}
                  {validationResult.canProceedWithValidItems && (
                    <Alert className="border-yellow-200 bg-yellow-50">
                      <AlertTriangle className="h-5 w-5 text-yellow-600" />
                      <AlertDescription className="text-yellow-800">
                        <div className="font-medium mb-2">You have options:</div>
                        <ul className="text-sm space-y-1">
                          <li>• Fix the errors in your file and try again</li>
                          <li>• Or proceed with uploading only the {validationResult.data?.valid?.length || 0} valid items</li>
                        </ul>
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Data Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Valid Items */}
                    <div className="bg-white rounded shadow-sm border border-green-200 p-6">
                      <h5 className="font-semibold text-green-900 mb-3 flex items-center">
                        <CheckCircle className="h-5 w-5 mr-2" />
                        Valid Items ({validationResult.data?.valid?.length || 0})
                      </h5>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {validationResult.data?.valid?.length > 0 ? (
                          validationResult.data.valid.slice(0, 5).map((item, idx) => (
                            <div key={idx} className="text-sm text-green-700 bg-green-50 rounded px-3 py-2">
                              {Object.values(item).join(' | ')}
                            </div>
                          ))
                        ) : (
                          <p className="text-slate-400 text-sm">No valid items found</p>
                        )}
                        {validationResult.data?.valid?.length > 5 && (
                          <p className="text-slate-400 text-xs">...and {validationResult.data.valid.length - 5} more</p>
                        )}
                      </div>
                    </div>

                    {/* Invalid Items */}
                    <div className="bg-white rounded shadow-sm border border-red-200 p-6">
                      <h5 className="font-semibold text-red-900 mb-3 flex items-center">
                        <XCircle className="h-5 w-5 mr-2" />
                        Invalid Items ({validationResult.data?.invalid?.length || 0})
                      </h5>
                      <div className="max-h-40 overflow-y-auto space-y-2">
                        {validationResult.data?.invalid?.length > 0 ? (
                          validationResult.data.invalid.slice(0, 5).map((item, idx) => (
                            <div key={idx} className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">
                              {Object.values(item.item || item).join(' | ')}
                            </div>
                          ))
                        ) : (
                          <p className="text-slate-400 text-sm">No invalid items</p>
                        )}
                        {validationResult.data?.invalid?.length > 5 && (
                          <p className="text-slate-400 text-xs">...and {validationResult.data.invalid.length - 5} more</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-3">
                    {validationResult.success ? (
                      <Button
                        onClick={() => handleUpload(false)}
                        className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white"
                        disabled={isUploading}
                      >
                        {isUploading ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        Upload All Items ({validationResult.data?.valid?.length || 0})
                      </Button>
                    ) : validationResult.canProceedWithValidItems ? (
                      <>
                        <Button
                          onClick={() => handleUpload(true)}
                          className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-white"
                          disabled={isUploading}
                        >
                          {isUploading ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <Upload className="h-4 w-4 mr-2" />
                          )}
                          Upload Valid Items Only ({validationResult.data?.valid?.length || 0})
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setStep(2)}
                          className="border-orange-200 text-orange-600 hover:bg-orange-50"
                        >
                          <ArrowLeft className="h-4 w-4 mr-2" />
                          Fix Errors & Try Again
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => setStep(2)}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Fix Errors Before Uploading
                      </Button>
                    )}
                    
                    <Button variant="outline" onClick={reset}>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload Another File
                    </Button>
                  </div>
                </div>
              )}

              {!validationResult && (
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Results */}
          {step === 4 && uploadResult && (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded shadow-sm border border-slate-200 p-8 text-center">
                <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ${
                  uploadResult.success 
                    ? 'bg-gradient-to-r from-green-500 to-green-600' 
                    : 'bg-gradient-to-r from-red-500 to-red-600'
                }`}>
                  {uploadResult.success ? (
                    <CheckCircle className="h-8 w-8 text-white" />
                  ) : (
                    <XCircle className="h-8 w-8 text-white" />
                  )}
                </div>
                
                <h3 className="text-xl font-semibold text-slate-900 mb-2">
                  {uploadResult.success ? 'Upload Completed!' : 'Upload Issues'}
                </h3>
                
                <p className={`mb-6 ${uploadResult.success ? 'text-green-700' : 'text-red-700'}`}>
                  {uploadResult.success 
                    ? `Successfully uploaded ${uploadResult.successfulItems} items.`
                    : `Failed to upload ${uploadResult.failedItems} items.`
                  }
                </p>

                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded p-4 mb-6 text-left">
                    <h5 className="font-medium text-red-900 mb-2">Upload Errors:</h5>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {uploadResult.errors.map((err, idx) => (
                        <div key={idx} className="text-sm text-red-700">{err}</div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-center gap-3">
                  <Button onClick={reset} className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Another File
                  </Button>
                  <Button variant="outline" onClick={() => { reset(); onClose(); }}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="border-t border-slate-200 pt-6">
          <Button variant="outline" onClick={() => { reset(); onClose(); }} className="border-slate-300">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
} 